import { CandidateRole } from "src/app/models/enum";
import { AssessmentConfigRes, ManeuverOutcomeDraft, ManeuverOutcomeValue, OBComments, TemCompetencyOutcomeDraft } from "src/app/models/models";
import { OutcomeSlot } from "./ts1-rating-outcome-interaction";
import { readTemCompetencyRatingC } from "./ts1-tem-competency-slots.utils";

export type Ts1ReviewRatingCell = number | "na" | "inc" | null;

/**
 * Per-competency review block (TS-1 v3 Step 3).
 * Rating A (How Many) and Rating B (How Often) are pre-calculated and read-only.
 * Rating C is the instructor's editable TEM input.
 * FINAL SCORE is the minimum of A, B, and C when all three are set; null when incomplete.
 */
export interface Ts1ReviewCompetencyBlock {
	code: string;
	ratingA: Ts1ReviewRatingCell;
	ratingB: Ts1ReviewRatingCell;
	ratingC: Ts1ReviewRatingCell;
	/** Numeric min of A/B/C when complete; null when any rating is missing; 0 for not-graded. */
	finalScore: Ts1ReviewRatingCell;
	/** No applicable OBs for this competency — disabled card with FINAL SCORE 0. */
	notGraded: boolean;
}

export interface Ts1ReviewManeuverRow {
	maneuverCode: string;
	label: string;
	pf: Ts1ReviewRatingCell;
	pm: Ts1ReviewRatingCell;
}

/** Canonical competency display order (fills the 2-column grid row-by-row). */
export const TS1_REVIEW_COMPETENCY_ORDER = ["KNO", "PRO", "COM", "FPA", "FPM", "LTW", "PSD", "SAW", "WLM"];

/** Step 3 legend labels for Rating A / B / C rows. */
export const TS1_REVIEW_RATING_LEGEND = [
	{ key: "A", label: "How Many" },
	{ key: "B", label: "How Often" },
	{ key: "C", label: "TEM Safety Outcome" },
] as const;

export function isCompetencyManeuverCode(code?: string | null): boolean {
	if (!code) return false;
	return TS1_REVIEW_COMPETENCY_ORDER.includes(code.toUpperCase());
}

export function formatTs1ReviewRatingCell(value: Ts1ReviewRatingCell): string {
	if (value == null) return "—";
	if (value === "na") return "N/A";
	if (value === "inc") return "Inc.";
	return String(value);
}

export function ts1ReviewCellClass(value: Ts1ReviewRatingCell): Record<string, boolean> {
	if (value == null) return {};
	if (value === "na") {
		return { "ts1-review-grid__cell--selected": true, "ts1-review-grid__cell--rate-na": true };
	}
	if (value === "inc") {
		return { "ts1-review-grid__cell--selected": true, "ts1-review-grid__cell--rate-inc": true };
	}
	return {
		"ts1-review-grid__cell--selected": true,
		[`ts1-review-grid__cell--rate-${value}`]: true,
	};
}

function competencyRatingCToCell(value: ManeuverOutcomeValue | null | undefined): Ts1ReviewRatingCell {
	if (value == null) return null;
	if (value === "na" || value === "inc") return null;
	if (typeof value === "number") return value > 0 ? value : null;
	return null;
}

function outcomeToCell(value: ManeuverOutcomeValue | null | undefined): Ts1ReviewRatingCell {
	if (value == null) return null;
	if (value === "na" || value === "inc") return value;
	if (typeof value === "number") {
		if (value < 0) return "na";
		if (value === 0) return null;
		return value;
	}
	return null;
}

/**
 * Role-based outcome slot for maneuver outcomes and evaluation OB restore.
 * Do not use for TEM competency Rating C — use ts1-tem-competency-slots.utils instead.
 */
export function outcomeSlotForRoleAndIndex(
	role: CandidateRole | undefined,
	candidateIndex: number,
	hasSecondCandidate: boolean,
): OutcomeSlot {
	const isPm = role === CandidateRole.PM;
	if (!hasSecondCandidate) {
		return isPm ? "pm" : "pfPrimary";
	}
	if (candidateIndex === 0) {
		return isPm ? "pm" : "pfPrimary";
	}
	return isPm ? "pmPrimary" : "pf";
}

/** Step 3 — PF column storage slot for the carousel-selected candidate. */
export function step3PfColumnOutcomeSlot(candidateIndex: number): OutcomeSlot {
	return candidateIndex === 0 ? "pfPrimary" : "pf";
}

/** Step 3 — PM column storage slot for the carousel-selected candidate. */
export function step3PmColumnOutcomeSlot(candidateIndex: number, hasSecondCandidate: boolean): OutcomeSlot {
	if (!hasSecondCandidate) return "pm";
	return candidateIndex === 0 ? "pm" : "pmPrimary";
}

/** Outcome slot for maneuver summary rows tied to crew role assignment. */
export function currentOutcomeSlotForCrewRoles(
	crewRoles: CandidateRole[],
	index: number,
	hasSecondCandidate: boolean,
): OutcomeSlot {
	return outcomeSlotForRoleAndIndex(crewRoles[index] ?? CandidateRole.PF, index, hasSecondCandidate);
}

export function buildTs1ReviewManeuverRows(params: {
	assessmentConfig: AssessmentConfigRes[];
	maneuverOutcomes: ManeuverOutcomeDraft[];
	currentIndex: number;
	hasSecondCandidate: boolean;
}): Ts1ReviewManeuverRow[] {
	const { assessmentConfig, maneuverOutcomes, currentIndex, hasSecondCandidate } = params;
	const pfSlot = step3PfColumnOutcomeSlot(currentIndex);
	const pmSlot = step3PmColumnOutcomeSlot(currentIndex, hasSecondCandidate);

	return assessmentConfig
		.map((assessment) => {
		const maneuverCode = assessment.maneuverType.code;
		if (isCompetencyManeuverCode(maneuverCode)) {
			return null;
		}
		const outcome = maneuverOutcomes.find((o) => o.maneuverCode === maneuverCode);
		const pf = outcomeToCell(outcome?.[pfSlot]);
		const pm = hasSecondCandidate ? outcomeToCell(outcome?.[pmSlot]) : outcomeToCell(outcome?.pm);

		return {
			maneuverCode,
			label: assessment.maneuverType.description?.toUpperCase() ?? maneuverCode,
			pf,
			pm,
		};
	})
		.filter((row): row is Ts1ReviewManeuverRow => row != null);
}

/**
 * Build the per-competency A/B/C blocks for the currently-selected carousel candidate.
 * Rating C uses candidate-index canonical TEM slots (not PF/PM role slots).
 */
export function buildTs1ReviewCompetencyBlocks(params: {
	howManyByCompetency: Map<string, number>;
	howOftenByCompetency: Map<string, number>;
	applicableByCompetency: Map<string, number>;
	temCompetencyOutcomes: TemCompetencyOutcomeDraft[];
	candidateIndex: number;
	hasSecondCandidate: boolean;
}): Ts1ReviewCompetencyBlock[] {
	const {
		howManyByCompetency,
		howOftenByCompetency,
		applicableByCompetency,
		temCompetencyOutcomes,
		candidateIndex,
		hasSecondCandidate,
	} = params;

	return TS1_REVIEW_COMPETENCY_ORDER.map((code) => {
		const notGraded = (applicableByCompetency.get(code) ?? 0) === 0;
		if (notGraded) {
			return {
				code,
				ratingA: null,
				ratingB: null,
				ratingC: null,
				finalScore: 0,
				notGraded: true,
			};
		}

		const override = temCompetencyOutcomes.find((o) => o.competencyCode === code);
		const ratingA = numericToCell(howManyByCompetency.get(code));
		const ratingB = numericToCell(howOftenByCompetency.get(code));
		const ratingC = competencyRatingCToCell(
			readTemCompetencyRatingC(override, candidateIndex, hasSecondCandidate),
		);
		return {
			code,
			ratingA,
			ratingB,
			ratingC,
			finalScore: computeTs1ReviewFinalScore(ratingA, ratingB, ratingC),
			notGraded: false,
		};
	});
}

/** True when all three competency ratings are positive numeric values (1–5). */
export function areTs1ReviewCompetencyRatingsComplete(
	ratingA: Ts1ReviewRatingCell,
	ratingB: Ts1ReviewRatingCell,
	ratingC: Ts1ReviewRatingCell,
): boolean {
	return [ratingA, ratingB, ratingC].every((cell) => typeof cell === "number" && cell > 0);
}

/** Min of A/B/C when all three are set; null when any rating is missing. */
export function computeTs1ReviewFinalScore(
	ratingA: Ts1ReviewRatingCell,
	ratingB: Ts1ReviewRatingCell,
	ratingC: Ts1ReviewRatingCell,
): Ts1ReviewRatingCell {
	if (!areTs1ReviewCompetencyRatingsComplete(ratingA, ratingB, ratingC)) {
		return null;
	}
	return Math.min(ratingA as number, ratingB as number, ratingC as number);
}

function numericToCell(value: number | undefined): Ts1ReviewRatingCell {
	if (value == null || Number.isNaN(value) || value <= 0) return null;
	return value;
}
